import { useEffect, useRef, useState, type ReactNode } from 'react'

/**
 * Valore numerico che, quando cambia (per esempio dopo un filtro), scorre dal valore precedente al nuovo.
 * Riconosce un solo numero in formato italiano dentro il testo ("€ 1.234,56", "12,5%", "42"): il resto del testo resta
 * fisso. Testo senza numeri, con più numeri (date, intervalli) o nodi React si mostrano così come sono.
 * Al primo render non anima; un valore che arriva da "—" (nessun dato) parte da 0. Con prefers-reduced-motion il
 * valore cambia subito. Le migliaia hanno sempre il punto, anche a fine animazione (1.234, non 1234). I lettori di
 * schermo leggono solo il valore finale.
 */
const NUM = /-?\d{1,3}(?:\.\d{3})+(?:,\d+)?|-?\d+(?:,\d+)?/g
const DURATION = 700

interface Parsed { before: string; after: string; n: number; decimals: number; grouped: boolean }

function parse(v: ReactNode): Parsed | null {
  if (typeof v === 'number') return Number.isFinite(v) ? parse(v.toLocaleString('it-IT')) : null
  if (typeof v !== 'string') return null
  const all = v.match(NUM)
  if (!all || all.length !== 1) return null
  const tok = all[0]
  const i = v.indexOf(tok)
  const [int = '', dec = ''] = tok.split(',')
  return { before: v.slice(0, i), after: v.slice(i + tok.length), n: Number(int.replaceAll('.', '') + (dec ? '.' + dec : '')), decimals: dec.length, grouped: tok.includes('.') || Math.abs(Number(int.replaceAll('.', ''))) >= 1000 }
}

function format(p: Parsed, n: number): string {
  return p.before + n.toLocaleString('it-IT', { minimumFractionDigits: p.decimals, maximumFractionDigits: p.decimals, useGrouping: p.grouped ? 'always' : false }) + p.after
}

function reducedMotion(): boolean {
  return typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
}

const easeOut = (t: number) => 1 - Math.pow(1 - t, 3)

export function AnimatedValue({ value }: { value: ReactNode }) {
  const target = parse(value)
  const [shown, setShown] = useState<string | null>(null)
  const last = useRef<number | null>(null)
  const mounted = useRef(false)
  const key = target ? `${target.before}|${target.n}|${target.decimals}|${target.after}` : null

  useEffect(() => {
    const first = !mounted.current
    mounted.current = true
    if (!target) { last.current = null; setShown(null); return }
    const from = last.current ?? (first ? null : 0)
    last.current = target.n
    if (from === null || from === target.n || reducedMotion()) { setShown(null); return }
    let raf = 0
    const start = performance.now()
    const step = (now: number) => {
      const t = Math.min(1, (now - start) / DURATION)
      if (t >= 1) { setShown(null); return }
      setShown(format(target, from + (target.n - from) * easeOut(t)))
      raf = requestAnimationFrame(step)
    }
    raf = requestAnimationFrame(step)
    return () => cancelAnimationFrame(raf)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])

  if (!target) return <>{value}</>
  const final = format(target, target.n)
  if (shown === null) return <>{final}</>
  return (
    <>
      <span aria-hidden="true">{shown}</span>
      <span className="md-sr-only">{final}</span>
    </>
  )
}
